import aiLogger from './ai-logger';
import { getCsrfToken } from '../services/ai-editor-utils';
/**
 * Personalização de tema do chat. Campos de cor declarados uma única vez em
 * THEME_FIELDS (cor nova = coluna no banco + entrada lá + swatch no HTML).
 * O tema salvo também é aplicado server-side (chat/parts/theme-style.blade.php) anti-FOUC.
 */

export interface ThemeOptions {
    savedBgColor:        string;
    savedTextColor:      string;
    savedCustomBg:       string;
    savedCustomText:     string;
    savedContainerBg:    string;
    savedCardBg:         string;
    savedBtnColor:       string;
    savedBubbleUser:     string;
    savedBubbleAi:       string;
    savedInputBg:        string;
    savedConvCardBg:     string;
    savedConvCardBorder: string;
    savedConvCardText:   string;
    savedConvCardBtn:    string;
    savedFileCardBg:     string;
    savedFileCardBorder: string;
    savedFileCardText:   string;
    savedFileCardBtn:    string;
    savedBubbleUserText: string;
    savedBubbleAiText:   string;
    savedLogoMode:       string;
    savedModalBg:        string;
    savedModalBackdrop:  string;
    savedModalBorder:    string;
    savedModalText:      string;
    savedModalHeaderBg:  string;
    savedConvCardActiveBg:     string;
    savedConvCardActiveBorder: string;
    savedConvCardActiveText:   string;
    savedFileCardActiveBg:     string;
    savedFileCardActiveBorder: string;
    savedFileCardActiveText:   string;
}

type ResetScope = 'all' | 'geral' | 'chat' | 'sidebars' | 'modals';

type ThemeState = Record<string, string | null>;

// Definição única de cada campo de cor (null em cssVar/group = tratamento especial).
interface ThemeFieldDef {
    key:     string;
    saved:   keyof ThemeOptions;
    payload: string;
    cssVar:  string | null;
    group:   string | null;
    scope:   Exclude<ResetScope, 'all'>;
}

const THEME_FIELDS: ThemeFieldDef[] = [
    { key: 'bgColor',         saved: 'savedBgColor',     payload: 'bg_color',          cssVar: null,                    group: 'bg',        scope: 'geral' },
    { key: 'customBgColor',   saved: 'savedCustomBg',    payload: 'custom_bg_color',   cssVar: null,                    group: null,        scope: 'geral' },
    { key: 'textColor',       saved: 'savedTextColor',   payload: 'text_color',        cssVar: null,                    group: 'text',      scope: 'geral' },
    { key: 'customTextColor', saved: 'savedCustomText',  payload: 'custom_text_color', cssVar: null,                    group: null,        scope: 'geral' },
    { key: 'containerBg',     saved: 'savedContainerBg', payload: 'container_bg',      cssVar: '--chat-container-bg',   group: 'container', scope: 'geral' },
    { key: 'btnColor',        saved: 'savedBtnColor',    payload: 'btn_color',         cssVar: '--chat-btn-color',      group: 'btn',       scope: 'geral' },
    { key: 'logoMode',        saved: 'savedLogoMode',    payload: 'logo_mode',         cssVar: null,                    group: null,        scope: 'geral' },

    { key: 'cardBg',          saved: 'savedCardBg',      payload: 'card_bg',           cssVar: '--chat-card-bg',        group: 'card',      scope: 'chat' },
    { key: 'bubbleUser',      saved: 'savedBubbleUser',  payload: 'bubble_user',       cssVar: '--chat-bubble-user',    group: 'bubbleUser', scope: 'chat' },
    { key: 'bubbleAi',        saved: 'savedBubbleAi',    payload: 'bubble_ai',         cssVar: '--chat-bubble-ai',      group: 'bubbleAi',  scope: 'chat' },
    { key: 'inputBg',         saved: 'savedInputBg',     payload: 'input_bg',          cssVar: '--chat-input-bg',       group: 'inputBg',   scope: 'chat' },
    { key: 'bubbleUserText',  saved: 'savedBubbleUserText', payload: 'bubble_user_text', cssVar: '--chat-bubble-user-text', group: 'bubbleUserText', scope: 'chat' },
    { key: 'bubbleAiText',    saved: 'savedBubbleAiText',   payload: 'bubble_ai_text',   cssVar: '--chat-bubble-ai-text',   group: 'bubbleAiText',   scope: 'chat' },

    { key: 'convCardBg',      saved: 'savedConvCardBg',     payload: 'conv_card_bg',     cssVar: '--chat-conv-card-bg',     group: 'convCardBg',     scope: 'sidebars' },
    { key: 'convCardBorder',  saved: 'savedConvCardBorder', payload: 'conv_card_border', cssVar: '--chat-conv-card-border', group: 'convCardBorder', scope: 'sidebars' },
    { key: 'convCardText',    saved: 'savedConvCardText',   payload: 'conv_card_text',   cssVar: '--chat-conv-card-text',   group: 'convCardText',   scope: 'sidebars' },
    { key: 'convCardBtn',     saved: 'savedConvCardBtn',    payload: 'conv_card_btn',    cssVar: '--chat-conv-card-btn',    group: 'convCardBtn',    scope: 'sidebars' },
    { key: 'fileCardBg',      saved: 'savedFileCardBg',     payload: 'file_card_bg',     cssVar: '--chat-file-card-bg',     group: 'fileCardBg',     scope: 'sidebars' },
    { key: 'fileCardBorder',  saved: 'savedFileCardBorder', payload: 'file_card_border', cssVar: '--chat-file-card-border', group: 'fileCardBorder', scope: 'sidebars' },
    { key: 'fileCardText',    saved: 'savedFileCardText',   payload: 'file_card_text',   cssVar: '--chat-file-card-text',   group: 'fileCardText',   scope: 'sidebars' },
    { key: 'fileCardBtn',     saved: 'savedFileCardBtn',    payload: 'file_card_btn',    cssVar: '--chat-file-card-btn',    group: 'fileCardBtn',    scope: 'sidebars' },
    { key: 'convCardActiveBg',     saved: 'savedConvCardActiveBg',     payload: 'conv_card_active_bg',     cssVar: '--chat-conv-card-active-bg',     group: 'convCardActiveBg',     scope: 'sidebars' },
    { key: 'convCardActiveBorder', saved: 'savedConvCardActiveBorder', payload: 'conv_card_active_border', cssVar: '--chat-conv-card-active-border', group: 'convCardActiveBorder', scope: 'sidebars' },
    { key: 'convCardActiveText',   saved: 'savedConvCardActiveText',   payload: 'conv_card_active_text',   cssVar: '--chat-conv-card-active-text',   group: 'convCardActiveText',   scope: 'sidebars' },
    { key: 'fileCardActiveBg',     saved: 'savedFileCardActiveBg',     payload: 'file_card_active_bg',     cssVar: '--chat-file-card-active-bg',     group: 'fileCardActiveBg',     scope: 'sidebars' },
    { key: 'fileCardActiveBorder', saved: 'savedFileCardActiveBorder', payload: 'file_card_active_border', cssVar: '--chat-file-card-active-border', group: 'fileCardActiveBorder', scope: 'sidebars' },
    { key: 'fileCardActiveText',   saved: 'savedFileCardActiveText',   payload: 'file_card_active_text',   cssVar: '--chat-file-card-active-text',   group: 'fileCardActiveText',   scope: 'sidebars' },

    { key: 'modalBg',        saved: 'savedModalBg',       payload: 'modal_bg',        cssVar: '--chat-modal-bg',        group: 'modalBg',       scope: 'modals' },
    { key: 'modalBackdrop',  saved: 'savedModalBackdrop', payload: 'modal_backdrop',  cssVar: '--chat-modal-backdrop',  group: 'modalBackdrop', scope: 'modals' },
    { key: 'modalBorder',    saved: 'savedModalBorder',   payload: 'modal_border',    cssVar: '--chat-modal-border',    group: 'modalBorder',   scope: 'modals' },
    { key: 'modalText',      saved: 'savedModalText',     payload: 'modal_text',      cssVar: '--chat-modal-text',      group: 'modalText',     scope: 'modals' },
    { key: 'modalHeaderBg',  saved: 'savedModalHeaderBg', payload: 'modal_header_bg', cssVar: '--chat-modal-header-bg', group: 'modalHeaderBg', scope: 'modals' },
];

// Mesmo formato do sanitizeColor do backend — senão a seleção "some" após salvar.
function norm(color: string | null | undefined): string {
    return (color || '').toLowerCase().replace(/\s+/g, '');
}

const PRESET_BG: string[] = [
    '#e8e8e8',
    '#f0f4ff',
    '#f5f0ff',
    '#111111',
    '#1a1a2e',
    '#0d1117',
];

const PRESET_TEXT: string[] = [
    '#1a1a1a',
    '#2c3e50',
    '#374151',
    '#e5e7eb',
    '#d1d5db',
    '#f3f4f6',
];

const PRESET_CARD: string[] = [
    '#ffffff',
    '#f9fafb',
    '#f0f4ff',
    '#131313',
    '#1c1c1e',
];
const PRESET_BUBBLE_USER: string[] = [
    '#e0ebfd',
    '#dbeafe',
    '#ede9fe',
    '#282a2c',
    '#1a2030',
    '#1e293b',
];

const PRESET_BUBBLE_AI: string[] = [
    '#ffffff',
    '#f9fafb',
    '#f0f4ff',
    '#131313',
    '#1e1e1e',
    '#0f172a',
];

const PRESET_INPUT: string[] = [
    'rgba(255,255,255,0.6)',
    'rgba(255,255,255,0.9)',
    '#ffffff',
    'rgba(30,30,30,0.6)',
    'rgba(17,17,17,0.8)',
];

const PRESET_CONV_CARD: string[] = [
    'rgba(255,255,255,0.3)',
    'rgba(255,255,255,0.6)',
    '#ffffff',
    'rgba(255,255,255,0.04)',
    'rgba(255,255,255,0.08)',
];

const PRESET_FILE_CARD: string[] = [
    'rgba(255,255,255,0.3)',
    'rgba(255,255,255,0.6)',
    '#ffffff',
    'rgba(255,255,255,0.04)',
    'rgba(255,255,255,0.08)',
];

const PRESET_CARD_BORDER: string[] = [
    'rgba(0,0,0,0.04)',
    'rgba(0,0,0,0.08)',
    'rgba(0,0,0,0.15)',
    'rgba(255,255,255,0.04)',
    'rgba(255,255,255,0.1)',
    'rgba(255,255,255,0.2)',
];

const PRESET_CARD_TEXT: string[] = [
    '#1a1a1a',
    '#374151',
    '#e5e7eb',
    '#f3f4f6',
    '#ffffff',
];

const PRESET_CARD_BTN: string[] = [
    'rgba(255,255,255,0.6)',
    'rgba(255,255,255,0.3)',
    '#ffffff',
    'rgba(255,255,255,0.06)',
    'rgba(255,255,255,0.12)',
];
const PRESET_BUBBLE_TEXT: string[] = [
    '#1a1a1a',
    '#282a2c',
    '#374151',
    '#e3e3e3',
    '#e5e7eb',
];
const PRESET_BTN: string[] = [
    '#1e1f20',
    '#2563eb',
    '#7c3aed',
    '#059669',
    '#dc2626',
];
const PRESET_MODAL_BACKDROP: string[] = [
    'rgba(0,0,0,0.45)',
    'rgba(0,0,0,0.15)',
    'rgba(0,0,0,0.85)',
    'rgba(255,255,255,0.1)',
];

// Valores sem espaços — o backend normaliza (sanitizeColor).
const THEME_LIGHT: ThemeState = {
    bgColor: '#e8e8e8', textColor: '#1a1a1a',
    customBgColor: null, customTextColor: null,
    containerBg: 'rgba(255,255,255,0.64)',
    cardBg: '#ffffff', btnColor: '#1e1f20',
    bubbleUser: '#e0ebfd', bubbleAi: '#ffffff',
    inputBg: 'rgba(255,255,255,0.6)',
    convCardBg: 'rgba(255,255,255,0.3)',
    convCardBorder: 'rgba(0,0,0,0.04)',
    convCardText: '#1a1a1a',
    convCardBtn: 'rgba(255,255,255,0.6)',
    fileCardBg: 'rgba(255,255,255,0.3)',
    fileCardBorder: 'rgba(0,0,0,0.04)',
    fileCardText: '#1a1a1a',
    fileCardBtn: 'rgba(255,255,255,0.6)',
    bubbleUserText: '#282a2c', bubbleAiText: '#1a1a1a',
    logoMode: 'dark',
    modalBg: 'rgba(255,255,255,0.92)',
    modalBackdrop: 'rgba(0,0,0,0.45)',
    modalBorder: 'rgba(255,255,255,0.3)',
    modalText: '#1a1a1a',
    modalHeaderBg: 'rgba(0,0,0,0.06)',
    convCardActiveBg: 'rgba(32,153,242,0.15)',
    convCardActiveBorder: '#2099f2',
    convCardActiveText: '#2099f2',
    fileCardActiveBg: 'rgba(32,153,242,0.15)',
    fileCardActiveBorder: '#2099f2',
    fileCardActiveText: '#2099f2',
};

const THEME_DARK: ThemeState = {
    bgColor: '#111111', textColor: '#e5e7eb',
    customBgColor: null, customTextColor: null,
    containerBg: 'rgba(0,0,0,0.24)',
    cardBg: '#131313', btnColor: '#2563eb',
    bubbleUser: '#282a2c', bubbleAi: '#131313',
    inputBg: 'rgba(30,30,30,0.6)',
    convCardBg: 'rgba(255,255,255,0.04)',
    convCardBorder: 'rgba(255,255,255,0.04)',
    convCardText: '#e5e7eb',
    convCardBtn: 'rgba(255,255,255,0.06)',
    fileCardBg: 'rgba(255,255,255,0.04)',
    fileCardBorder: 'rgba(255,255,255,0.04)',
    fileCardText: '#e5e7eb',
    fileCardBtn: 'rgba(255,255,255,0.06)',
    bubbleUserText: '#e3e3e3', bubbleAiText: '#e5e7eb',
    logoMode: 'light',
    modalBg: 'rgba(30,30,30,0.95)',
    modalBackdrop: 'rgba(0,0,0,0.65)',
    modalBorder: 'rgba(255,255,255,0.08)',
    modalText: '#eeeeee',
    modalHeaderBg: 'rgba(255,255,255,0.05)',
    convCardActiveBg: 'rgba(32,110,167,0.2)',
    convCardActiveBorder: '#206ea7',
    convCardActiveText: '#2099f2',
    fileCardActiveBg: 'rgba(32,110,167,0.2)',
    fileCardActiveBorder: '#206ea7',
    fileCardActiveText: '#2099f2',
};

export class ChatThemeModal {
    private overlay: HTMLDivElement | null = null;
    private state: ThemeState;
    private onClose: (() => void) | null = null;
    private containerAlpha: number;
    private containerBaseColor: string;
    private saveDebounceTimer: ReturnType<typeof setTimeout> | null = null;

    constructor(private options: ThemeOptions) {
        this.state = {};
        for (const field of THEME_FIELDS) {
            this.state[field.key] = (options[field.saved] as string) || null;
        }
        // logoMode aceita apenas light/dark; 'auto' (ou qualquer outro) vira null
        if (!['light', 'dark'].includes(this.state.logoMode || '')) {
            this.state.logoMode = null;
        }

        const containerDefaults = this.defaultContainerBase();
        this.containerAlpha     = containerDefaults.alpha;
        this.containerBaseColor = containerDefaults.base;

        if (this.state.containerBg) {
            const parsed = this.parseRgba(this.state.containerBg);
            if (parsed) {
                this.containerAlpha     = parsed.a;
                this.containerBaseColor = this.rgbToHex(parsed.r, parsed.g, parsed.b);
            }
        }

        this.applyThemeToDOM(this.state);
    }

    // Evita que o slider de translucidez pinte painéis escuros de branco.
    private defaultContainerBase(): { base: string; alpha: number } {
        const isDark = document.documentElement.classList.contains('dark-mode');
        return isDark
            ? { base: '#000000', alpha: 0.24 }
            : { base: '#ffffff', alpha: 0.64 };
    }

    open(onCloseCallback?: () => void): void {
        if (this.overlay) return;
        this.onClose = onCloseCallback || null;
        this.overlay = this.buildModal();
        document.body.appendChild(this.overlay);
        requestAnimationFrame(() => this.overlay?.classList.add('visible'));
    }

    close(): void {
        if (!this.overlay) return;

        if (this.saveDebounceTimer) {
            clearTimeout(this.saveDebounceTimer);
            this.saveDebounceTimer = null;
            this.saveTheme();
        }

        this.overlay.classList.remove('visible');
        const el = this.overlay;
        setTimeout(() => el.remove(), 220);
        this.overlay = null;
        this.onClose?.();
    }

    // Reconstrói o modal aberto preservando a aba ativa (após preset ou reset).
    private rebuildOpenModal(): void {
        if (!this.overlay) return;

        const activeTab = this.overlay.querySelector<HTMLButtonElement>('.chat-theme-tab.active')?.dataset.tab || 'presets';
        const old = this.overlay;
        old.remove();

        this.overlay = this.buildModal();
        this.overlay.classList.add('visible');
        document.body.appendChild(this.overlay);

        this.overlay.querySelectorAll('.chat-theme-tab').forEach(t => t.classList.remove('active'));
        this.overlay.querySelectorAll('.chat-theme-tab-content').forEach(c => c.classList.remove('active'));
        this.overlay.querySelector(`.chat-theme-tab[data-tab="${activeTab}"]`)?.classList.add('active');
        this.overlay.querySelector(`.chat-theme-tab-content[data-panel="${activeTab}"]`)?.classList.add('active');
    }

    private buildModal(): HTMLDivElement {
        const overlay = document.createElement('div');
        overlay.className = 'chat-theme-overlay';

        const effectiveBg  = norm(this.state.customBgColor   || this.state.bgColor);
        const effectiveTxt = norm(this.state.customTextColor || this.state.textColor);

        overlay.innerHTML = `
            <div class="chat-theme-modal">
                <div class="chat-theme-modal-header">
                    <span class="chat-theme-modal-title">
                        <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none"
                            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <circle cx="12" cy="12" r="10"></circle>
                            <circle cx="12" cy="12" r="3" fill="currentColor"></circle>
                            <line x1="12" y1="2" x2="12" y2="5"></line>
                            <line x1="12" y1="19" x2="12" y2="22"></line>
                            <line x1="2" y1="12" x2="5" y2="12"></line>
                            <line x1="19" y1="12" x2="22" y2="12"></line>
                        </svg>
                        Tema
                    </span>
                    <button type="button" class="chat-theme-close-btn" aria-label="Fechar">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
                            stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                    </button>
                </div>

                <div class="chat-theme-tabs">
                    <button type="button" class="chat-theme-tab active" data-tab="presets">Temas</button>
                    <button type="button" class="chat-theme-tab" data-tab="geral">Geral</button>
                    <button type="button" class="chat-theme-tab" data-tab="chat">Chat</button>
                    <button type="button" class="chat-theme-tab" data-tab="sidebars">Sidebars</button>
                    <button type="button" class="chat-theme-tab" data-tab="modals">Modais</button>
                </div>

                <!-- Aba: Temas ------------------------------------------------ -->
                <div class="chat-theme-tab-content active" data-panel="presets">
                    <div class="chat-theme-preset-grid">
                        <button type="button" class="chat-theme-preset-btn" data-preset="light">
                            <div class="chat-theme-preset-preview chat-theme-preset-preview--light">
                                <div class="preset-aside"></div>
                                <div class="preset-chat">
                                    <div class="preset-bubble"></div>
                                    <div class="preset-bubble preset-bubble--user"></div>
                                </div>
                                <div class="preset-aside"></div>
                            </div>
                            <span>Light</span>
                        </button>
                        <button type="button" class="chat-theme-preset-btn" data-preset="dark">
                            <div class="chat-theme-preset-preview chat-theme-preset-preview--dark">
                                <div class="preset-aside"></div>
                                <div class="preset-chat">
                                    <div class="preset-bubble"></div>
                                    <div class="preset-bubble preset-bubble--user"></div>
                                </div>
                                <div class="preset-aside"></div>
                            </div>
                            <span>Dark</span>
                        </button>
                    </div>
                    <button type="button" class="chat-theme-reset-btn" data-reset-scope="all" style="margin-top:12px;">Restaurar padrão (tudo)</button>
                </div>

                <!-- Aba: Geral ------------------------------------------------ -->
                <div class="chat-theme-tab-content" data-panel="geral">

                    <div class="chat-theme-section">
                        <span class="chat-theme-section-label">Fundo da Página</span>
                        <div class="chat-theme-color-row" data-group="bg">
                            ${PRESET_BG.map(c => this.buildColorSwatch(c, 'bg', effectiveBg === norm(c))).join('')}
                            ${this.buildCustomSwatch('bg', this.state.customBgColor, !!(this.state.customBgColor && effectiveBg === norm(this.state.customBgColor)))}
                        </div>
                    </div>

                    <div class="chat-theme-section">
                        <span class="chat-theme-section-label">Painéis laterais e Central</span>
                        <div class="chat-theme-color-row" data-group="container">
                            ${this.buildContainerBaseSwatches()}
                        </div>
                        <div class="chat-theme-alpha-row">
                            <span class="chat-theme-alpha-label">Translucidez</span>
                            <input type="range" class="chat-theme-alpha-slider" id="containerAlphaSlider"
                                min="0" max="100" value="${Math.round(this.containerAlpha * 100)}">
                            <span class="chat-theme-alpha-value" id="containerAlphaValue">${Math.round(this.containerAlpha * 100)}%</span>
                        </div>
                    </div>

                    <div class="chat-theme-section">
                        <span class="chat-theme-section-label">Texto e Ícones</span>
                        <div class="chat-theme-color-row" data-group="text">
                            ${PRESET_TEXT.map(c => this.buildColorSwatch(c, 'text', effectiveTxt === norm(c))).join('')}
                            ${this.buildCustomSwatch('text', this.state.customTextColor, !!(this.state.customTextColor && effectiveTxt === norm(this.state.customTextColor)))}
                        </div>
                    </div>

                    <div class="chat-theme-section">
                        <span class="chat-theme-section-label">Botões</span>
                        <div class="chat-theme-color-row" data-group="btn">
                            ${this.buildSwatchRow(PRESET_BTN, 'btn', 'btnColor')}
                        </div>
                    </div>

                    <div class="chat-theme-section">
                        <span class="chat-theme-section-label">Logo Athena</span>
                        <div class="chat-theme-logo-toggle">
                            <button type="button"
                                class="chat-theme-logo-btn${this.state.logoMode === 'light' ? ' active' : ''}"
                                data-logo-mode="light"
                                title="Logo branca (ideal para fundos escuros)">
                                🌙 Branca
                            </button>
                            <button type="button"
                                class="chat-theme-logo-btn${this.state.logoMode === 'dark' ? ' active' : ''}"
                                data-logo-mode="dark"
                                title="Logo escura/original (ideal para fundos claros)">
                                ☀️ Original
                            </button>
                            <button type="button"
                                class="chat-theme-logo-btn${!this.state.logoMode ? ' active' : ''}"
                                data-logo-mode="auto"
                                title="Adapta automaticamente conforme o modo claro/escuro">
                                🔄 Auto
                            </button>
                        </div>
                    </div>

                    <button type="button" class="chat-theme-reset-btn" data-reset-scope="geral">Restaurar padrão desta aba</button>
                </div>

                <!-- Aba: Chat ------------------------------------------------- -->
                <div class="chat-theme-tab-content" data-panel="chat">

                    <div class="chat-theme-preview" id="chat-theme-preview-box">
                        <div class="chat-theme-preview-bubble chat-theme-preview-bubble--ai">Olá! Sou a Athena.</div>
                        <div class="chat-theme-preview-bubble chat-theme-preview-bubble--user">Oi, tudo bem?</div>
                    </div>

                    <div class="chat-theme-section">
                        <span class="chat-theme-section-label">Bolha — IA (Fundo)</span>
                        <div class="chat-theme-color-row" data-group="bubbleAi">
                            ${this.buildSwatchRow(PRESET_BUBBLE_AI, 'bubbleAi', 'bubbleAi')}
                        </div>
                    </div>

                    <div class="chat-theme-section">
                        <span class="chat-theme-section-label">Bolha — IA (Texto)</span>
                        <div class="chat-theme-color-row" data-group="bubbleAiText">
                            ${this.buildSwatchRow(PRESET_BUBBLE_TEXT, 'bubbleAiText', 'bubbleAiText')}
                        </div>
                    </div>

                    <div class="chat-theme-section">
                        <span class="chat-theme-section-label">Bolha — Usuário (Fundo)</span>
                        <div class="chat-theme-color-row" data-group="bubbleUser">
                            ${this.buildSwatchRow(PRESET_BUBBLE_USER, 'bubbleUser', 'bubbleUser')}
                        </div>
                    </div>

                    <div class="chat-theme-section">
                        <span class="chat-theme-section-label">Bolha — Usuário (Texto)</span>
                        <div class="chat-theme-color-row" data-group="bubbleUserText">
                            ${this.buildSwatchRow(PRESET_BUBBLE_TEXT, 'bubbleUserText', 'bubbleUserText')}
                        </div>
                    </div>

                    <div class="chat-theme-section">
                        <span class="chat-theme-section-label">Input de Mensagem</span>
                        <div class="chat-theme-color-row" data-group="inputBg">
                            ${this.buildSwatchRow(PRESET_INPUT, 'inputBg', 'inputBg')}
                        </div>
                    </div>

                    <div class="chat-theme-section">
                        <span class="chat-theme-section-label">Cards de Mensagem</span>
                        <div class="chat-theme-color-row" data-group="card">
                            ${this.buildSwatchRow(PRESET_CARD, 'card', 'cardBg')}
                        </div>
                    </div>

                    <button type="button" class="chat-theme-reset-btn" data-reset-scope="chat">Restaurar padrão desta aba</button>
                </div>

                <!-- Aba: Sidebars --------------------------------------------- -->
                <div class="chat-theme-tab-content" data-panel="sidebars">

                    <div class="chat-theme-card-group">
                        <div class="chat-theme-card-group-title">Cards de Conversa</div>

                        <div class="chat-theme-section">
                            <span class="chat-theme-section-label">Fundo</span>
                            <div class="chat-theme-color-row" data-group="convCardBg">
                                ${this.buildSwatchRow(PRESET_CONV_CARD, 'convCardBg', 'convCardBg')}
                            </div>
                        </div>

                        <div class="chat-theme-section">
                            <span class="chat-theme-section-label">Borda</span>
                            <div class="chat-theme-color-row" data-group="convCardBorder">
                                ${this.buildSwatchRow(PRESET_CARD_BORDER, 'convCardBorder', 'convCardBorder')}
                            </div>
                        </div>

                        <div class="chat-theme-section">
                            <span class="chat-theme-section-label">Texto</span>
                            <div class="chat-theme-color-row" data-group="convCardText">
                                ${this.buildSwatchRow(PRESET_CARD_TEXT, 'convCardText', 'convCardText')}
                            </div>
                        </div>

                        <div class="chat-theme-section">
                            <span class="chat-theme-section-label">Botões</span>
                            <div class="chat-theme-color-row" data-group="convCardBtn">
                                ${this.buildSwatchRow(PRESET_CARD_BTN, 'convCardBtn', 'convCardBtn')}
                            </div>
                        </div>

                        <div class="chat-theme-section">
                            <span class="chat-theme-section-label">Fundo (Ativo)</span>
                            <div class="chat-theme-color-row" data-group="convCardActiveBg">
                                ${this.buildSwatchRow(PRESET_CONV_CARD, 'convCardActiveBg', 'convCardActiveBg')}
                            </div>
                        </div>

                        <div class="chat-theme-section">
                            <span class="chat-theme-section-label">Borda (Ativo)</span>
                            <div class="chat-theme-color-row" data-group="convCardActiveBorder">
                                ${this.buildSwatchRow(PRESET_CARD_BORDER, 'convCardActiveBorder', 'convCardActiveBorder')}
                            </div>
                        </div>

                        <div class="chat-theme-section">
                            <span class="chat-theme-section-label">Texto (Ativo)</span>
                            <div class="chat-theme-color-row" data-group="convCardActiveText">
                                ${this.buildSwatchRow(PRESET_CARD_TEXT, 'convCardActiveText', 'convCardActiveText')}
                            </div>
                        </div>
                    </div>

                    <div class="chat-theme-card-group">
                        <div class="chat-theme-card-group-title">Cards de Arquivo</div>

                        <div class="chat-theme-section">
                            <span class="chat-theme-section-label">Fundo</span>
                            <div class="chat-theme-color-row" data-group="fileCardBg">
                                ${this.buildSwatchRow(PRESET_FILE_CARD, 'fileCardBg', 'fileCardBg')}
                            </div>
                        </div>

                        <div class="chat-theme-section">
                            <span class="chat-theme-section-label">Borda</span>
                            <div class="chat-theme-color-row" data-group="fileCardBorder">
                                ${this.buildSwatchRow(PRESET_CARD_BORDER, 'fileCardBorder', 'fileCardBorder')}
                            </div>
                        </div>

                        <div class="chat-theme-section">
                            <span class="chat-theme-section-label">Texto</span>
                            <div class="chat-theme-color-row" data-group="fileCardText">
                                ${this.buildSwatchRow(PRESET_CARD_TEXT, 'fileCardText', 'fileCardText')}
                            </div>
                        </div>

                        <div class="chat-theme-section">
                            <span class="chat-theme-section-label">Botões</span>
                            <div class="chat-theme-color-row" data-group="fileCardBtn">
                                ${this.buildSwatchRow(PRESET_CARD_BTN, 'fileCardBtn', 'fileCardBtn')}
                            </div>
                        </div>

                        <div class="chat-theme-section">
                            <span class="chat-theme-section-label">Fundo (Ativo)</span>
                            <div class="chat-theme-color-row" data-group="fileCardActiveBg">
                                ${this.buildSwatchRow(PRESET_FILE_CARD, 'fileCardActiveBg', 'fileCardActiveBg')}
                            </div>
                        </div>

                        <div class="chat-theme-section">
                            <span class="chat-theme-section-label">Borda (Ativo)</span>
                            <div class="chat-theme-color-row" data-group="fileCardActiveBorder">
                                ${this.buildSwatchRow(PRESET_CARD_BORDER, 'fileCardActiveBorder', 'fileCardActiveBorder')}
                            </div>
                        </div>

                        <div class="chat-theme-section">
                            <span class="chat-theme-section-label">Texto (Ativo)</span>
                            <div class="chat-theme-color-row" data-group="fileCardActiveText">
                                ${this.buildSwatchRow(PRESET_CARD_TEXT, 'fileCardActiveText', 'fileCardActiveText')}
                            </div>
                        </div>
                    </div>

                    <button type="button" class="chat-theme-reset-btn" data-reset-scope="sidebars">Restaurar padrão desta aba</button>
                </div>

                <!-- Aba: Modais ----------------------------------------------- -->
                <div class="chat-theme-tab-content" data-panel="modals">

                    <div class="chat-theme-section">
                        <span class="chat-theme-section-label">Fundo do Modal</span>
                        <div class="chat-theme-color-row" data-group="modalBg">
                            ${this.buildSwatchRow(PRESET_CARD, 'modalBg', 'modalBg')}
                        </div>
                    </div>

                    <div class="chat-theme-section">
                        <span class="chat-theme-section-label">Overlay (Backdrop)</span>
                        <div class="chat-theme-color-row" data-group="modalBackdrop">
                            ${this.buildSwatchRow(PRESET_MODAL_BACKDROP, 'modalBackdrop', 'modalBackdrop')}
                        </div>
                    </div>

                    <div class="chat-theme-section">
                        <span class="chat-theme-section-label">Borda do Modal</span>
                        <div class="chat-theme-color-row" data-group="modalBorder">
                            ${this.buildSwatchRow(PRESET_CARD_BORDER, 'modalBorder', 'modalBorder')}
                        </div>
                    </div>

                    <div class="chat-theme-section">
                        <span class="chat-theme-section-label">Texto do Modal</span>
                        <div class="chat-theme-color-row" data-group="modalText">
                            ${this.buildSwatchRow(PRESET_TEXT, 'modalText', 'modalText')}
                        </div>
                    </div>

                    <div class="chat-theme-section">
                        <span class="chat-theme-section-label">Cabeçalho do Modal</span>
                        <div class="chat-theme-color-row" data-group="modalHeaderBg">
                            ${this.buildSwatchRow(PRESET_CONV_CARD, 'modalHeaderBg', 'modalHeaderBg')}
                        </div>
                    </div>

                    <button type="button" class="chat-theme-reset-btn" data-reset-scope="modals">Restaurar padrão desta aba</button>
                </div>
            </div>
        `;

        overlay.addEventListener('click', (e) => { if (e.target === overlay) this.close(); });
        overlay.querySelector('.chat-theme-close-btn')?.addEventListener('click', () => this.close());
        overlay.querySelectorAll<HTMLButtonElement>('.chat-theme-reset-btn').forEach(btn => {
            btn.addEventListener('click', () => this.resetTheme((btn.dataset.resetScope as ResetScope) || 'all'));
        });

        this.bindTabEvents(overlay);
        this.bindPresetEvents(overlay);
        this.bindSwatchEvents(overlay);
        this.bindAlphaSlider(overlay);
        this.bindLogoToggle(overlay);
        this.updatePreview(overlay);

        return overlay;
    }

    private buildSwatchRow(presets: string[], group: string, stateKey: string): string {
        const current = norm(this.state[stateKey]);
        const matchesPreset = presets.some(c => norm(c) === current);
        const customColor = (!matchesPreset && this.state[stateKey]) ? this.state[stateKey] : null;

        return presets.map(c => this.buildColorSwatch(c, group, current !== '' && norm(c) === current)).join('')
            + this.buildCustomSwatch(group, customColor, !!customColor);
    }

    private bindTabEvents(overlay: HTMLDivElement): void {
        overlay.querySelectorAll<HTMLButtonElement>('.chat-theme-tab').forEach(tab => {
            tab.addEventListener('click', () => {
                const target = tab.dataset.tab!;
                overlay.querySelectorAll('.chat-theme-tab').forEach(t => t.classList.remove('active'));
                overlay.querySelectorAll('.chat-theme-tab-content').forEach(c => c.classList.remove('active'));
                tab.classList.add('active');
                overlay.querySelector(`.chat-theme-tab-content[data-panel="${target}"]`)?.classList.add('active');
            });
        });
    }

    private bindPresetEvents(overlay: HTMLDivElement): void {
        overlay.querySelectorAll<HTMLButtonElement>('.chat-theme-preset-btn').forEach(btn => {
            btn.addEventListener('click', () => {
                const theme = btn.dataset.preset === 'dark' ? THEME_DARK : THEME_LIGHT;
                this.state = { ...theme };
                if (this.state.containerBg) {
                    const parsed = this.parseRgba(this.state.containerBg);
                    if (parsed) {
                        this.containerAlpha     = parsed.a;
                        this.containerBaseColor = this.rgbToHex(parsed.r, parsed.g, parsed.b);
                    }
                }
                this.applyThemeToDOM(this.state);
                this.scheduleSave();
                this.rebuildOpenModal();
            });
        });
    }

    private bindAlphaSlider(overlay: HTMLDivElement): void {
        const slider = overlay.querySelector<HTMLInputElement>('#containerAlphaSlider');
        const label  = overlay.querySelector<HTMLSpanElement>('#containerAlphaValue');
        if (!slider || !label) return;
        slider.addEventListener('input', () => {
            this.containerAlpha = parseInt(slider.value, 10) / 100;
            label.textContent   = slider.value + '%';
            this.rebuildContainerBg();
            this.applyThemeToDOM(this.state);
            this.scheduleSave();
        });
    }

    private rebuildContainerBg(): void {
        const hex = this.containerBaseColor.replace('#', '');
        const r = parseInt(hex.slice(0, 2), 16);
        const g = parseInt(hex.slice(2, 4), 16);
        const b = parseInt(hex.slice(4, 6), 16);
        this.state.containerBg = `rgba(${r},${g},${b},${this.containerAlpha.toFixed(2)})`;
    }

    private buildContainerBaseSwatches(): string {
        const bases = ['#ffffff', '#f0f4ff', '#f5f0ff', '#000000', '#111111'];
        const swatches = bases.map(c => {
            const active = this.containerBaseColor === c;
            const isLight = ['#ffffff', '#f0f4ff', '#f5f0ff'].includes(c);
            const isDark  = ['#000000', '#111111'].includes(c);
            let borderStyle = '';
            if (isLight) borderStyle = 'border:1px solid #ddd;';
            if (isDark)  borderStyle = 'border:1px solid rgba(255,255,255,0.25);';
            return `<button type="button"
                class="chat-theme-swatch${active ? ' active' : ''}"
                data-group="container"
                data-color="${c}"
                style="background:${c};${borderStyle}"
                title="${c}"></button>`;
        }).join('');

        const customBase = !bases.includes(this.containerBaseColor) ? this.containerBaseColor : null;
        return swatches + this.buildCustomSwatch('container', customBase, !!customBase);
    }

    private buildColorSwatch(color: string, group: string, active: boolean): string {
        const lightColors = ['#ffffff', '#f9fafb', '#f0f4ff', '#f5f0ff', '#f0fff4', '#e8e8e8'];
        const needsBorder = lightColors.includes(color);
        return `<button type="button"
            class="chat-theme-swatch${active ? ' active' : ''}"
            data-group="${group}" data-color="${color}"
            style="background:${color};${needsBorder ? 'border:1px solid #ddd;' : ''}"
            title="${color}"></button>`;
    }

    private buildCustomSwatch(group: string, savedColor: string | null, active: boolean): string {
        if (savedColor) {
            return `
                <button type="button"
                    class="chat-theme-swatch chat-theme-swatch-custom${active ? ' active' : ''}"
                    data-group="${group}" data-color="${savedColor}" data-is-custom="true"
                    style="background:${savedColor};" title="Editar cor personalizada">
                    <span class="chat-theme-custom-edit-icon">
                        <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none"
                            stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                        </svg>
                    </span>
                    <input type="color" class="chat-theme-color-picker" data-group="${group}" value="${this.toHexOrDefault(savedColor)}" tabindex="-1">
                </button>`;
        }
        return `
            <button type="button"
                class="chat-theme-swatch chat-theme-swatch-add"
                data-group="${group}" data-is-add="true" title="Adicionar cor personalizada">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                    <line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
                <input type="color" class="chat-theme-color-picker" data-group="${group}" value="#ffffff" tabindex="-1">
            </button>`;
    }

    /** input[type=color] só aceita hex de 6 dígitos. */
    private toHexOrDefault(color: string): string {
        if (/^#[0-9a-fA-F]{6}$/.test(color)) return color;
        const parsed = this.parseRgba(color);
        if (parsed) return this.rgbToHex(parsed.r, parsed.g, parsed.b);
        return '#ffffff';
    }

    private bindSwatchEvents(overlay: HTMLDivElement): void {
        overlay.addEventListener('click', (e) => {
            if ((e.target as HTMLElement).tagName === 'INPUT') return;
            const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('.chat-theme-swatch');
            if (!btn) return;
            if (btn.dataset.isAdd === 'true' || btn.dataset.isCustom === 'true') {
                e.stopPropagation();
                btn.querySelector<HTMLInputElement>('.chat-theme-color-picker')?.click();
                return;
            }
            this.selectColor(overlay, btn.dataset.group!, btn.dataset.color!, false);
        });

        overlay.addEventListener('change', (e) => {
            const picker = e.target as HTMLElement;
            if (!picker.classList.contains('chat-theme-color-picker')) return;
            const input = picker as HTMLInputElement;
            this.selectColor(overlay, input.dataset.group!, input.value, true);
            this.replaceWithCustomSwatch(overlay, input.dataset.group!, input.value);
        });
    }

    private selectColor(overlay: HTMLDivElement, group: string, color: string, isCustom: boolean): void {
        overlay.querySelectorAll<HTMLButtonElement>(`.chat-theme-swatch[data-group="${group}"]`)
            .forEach(b => b.classList.remove('active'));

        if (group === 'bg') {
            if (isCustom) { this.state.customBgColor = color; this.state.bgColor = null; }
            else          { this.state.bgColor = color; this.state.customBgColor = null; }
        } else if (group === 'text') {
            if (isCustom) { this.state.customTextColor = color; this.state.textColor = null; }
            else          { this.state.textColor = color; this.state.customTextColor = null; }
        } else if (group === 'container') {
            this.containerBaseColor = color;
            this.rebuildContainerBg();
        } else {
            const field = THEME_FIELDS.find(f => f.group === group);
            if (!field) {
                aiLogger.warn('[ChatThemeModal] Grupo de swatch desconhecido:', group);
                return;
            }
            this.state[field.key] = color;
        }

        overlay.querySelector<HTMLButtonElement>(
            `.chat-theme-swatch[data-group="${group}"][data-color="${color}"]`
        )?.classList.add('active');

        this.applyThemeToDOM(this.state);
        this.updatePreview(overlay);
        this.scheduleSave();
    }

    private replaceWithCustomSwatch(overlay: HTMLDivElement, group: string, color: string): void {
        const row = overlay.querySelector<HTMLDivElement>(`.chat-theme-color-row[data-group="${group}"]`);
        if (!row) return;
        const existing = row.querySelector<HTMLButtonElement>('.chat-theme-swatch-add, .chat-theme-swatch-custom');
        if (!existing) return;
        const wasActive = existing.classList.contains('active');
        existing.outerHTML = this.buildCustomSwatch(group, color, true);
        const newBtn = row.querySelector<HTMLButtonElement>('.chat-theme-swatch-custom');
        if (newBtn && wasActive) newBtn.classList.add('active');
    }

    private bindLogoToggle(overlay: HTMLDivElement): void {
        overlay.querySelectorAll<HTMLButtonElement>('.chat-theme-logo-btn').forEach(btn => {
            btn.addEventListener('click', () => {
                const mode = btn.dataset.logoMode as 'light' | 'dark' | 'auto';
                this.state.logoMode = mode === 'auto' ? null : mode;
                overlay.querySelectorAll('.chat-theme-logo-btn').forEach(b => b.classList.remove('active'));
                btn.classList.add('active');
                this.applyThemeToDOM(this.state);
                this.scheduleSave();
            });
        });
    }

    // bg/texto/logo têm tratamento especial — mesma lógica do partial server-side theme-style.
    private applyThemeToDOM(state: ThemeState): void {
        const html = document.documentElement;
        const bg  = state.customBgColor   || state.bgColor;
        const txt = state.customTextColor || state.textColor;

        if (bg)  { html.style.backgroundColor = bg; }
        else     { html.style.removeProperty('background-color'); }

        if (txt) { html.style.setProperty('--chat-text-color', txt); }
        else     { html.style.removeProperty('--chat-text-color'); }

        for (const field of THEME_FIELDS) {
            if (!field.cssVar) continue;
            const value = state[field.key];
            if (value) { html.style.setProperty(field.cssVar, value); }
            else       { html.style.removeProperty(field.cssVar); }
        }

        if (state.logoMode === 'light')     { html.style.setProperty('--chat-logo-filter', 'brightness(0) invert(1)'); }
        else if (state.logoMode === 'dark') { html.style.setProperty('--chat-logo-filter', 'none'); }
        else                                { html.style.removeProperty('--chat-logo-filter'); }
    }

    private updatePreview(overlay: HTMLDivElement): void {
        const box = overlay.querySelector<HTMLDivElement>('#chat-theme-preview-box');
        if (!box) return;
        box.style.backgroundColor = this.state.customBgColor || this.state.bgColor || '';
        const aiEl   = box.querySelector<HTMLElement>('.chat-theme-preview-bubble--ai');
        const userEl = box.querySelector<HTMLElement>('.chat-theme-preview-bubble--user');
        if (aiEl) {
            aiEl.style.backgroundColor = this.state.bubbleAi || '#ffffff';
            aiEl.style.color = this.state.bubbleAiText || '#1a1a1a';
        }
        if (userEl) {
            userEl.style.backgroundColor = this.state.bubbleUser || '#e0ebfd';
            userEl.style.color = this.state.bubbleUserText || '#282a2c';
        }
    }

    private resetTheme(scope: ResetScope): void {
        if (scope === 'all') {
            if (!window.confirm('Restaurar o tema padrão? Todas as personalizações serão removidas.')) {
                return;
            }
        }

        for (const field of THEME_FIELDS) {
            if (scope === 'all' || field.scope === scope) {
                this.state[field.key] = null;
            }
        }

        if (scope === 'all' || scope === 'geral') {
            const defaults = this.defaultContainerBase();
            this.containerAlpha     = defaults.alpha;
            this.containerBaseColor = defaults.base;
        }

        this.applyThemeToDOM(this.state);
        this.saveTheme();
        this.rebuildOpenModal();
    }

    private scheduleSave(): void {
        if (this.saveDebounceTimer) clearTimeout(this.saveDebounceTimer);
        this.saveDebounceTimer = setTimeout(() => {
            this.saveDebounceTimer = null;
            this.saveTheme();
        }, 400);
    }

    private saveTheme(): void {
        const payload: Record<string, string | null> = {};
        for (const field of THEME_FIELDS) {
            payload[field.payload] = this.state[field.key];
        }
        payload.logo_mode = this.state.logoMode ?? 'auto';

        fetch('/chat/theme', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': getCsrfToken(),
                'Accept': 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
            },
            credentials: 'same-origin',
            body: JSON.stringify(payload),
        }).catch((err) => aiLogger.error('[ChatThemeModal] Erro ao salvar tema:', err));
    }

    private parseRgba(value: string): { r: number; g: number; b: number; a: number } | null {
        const m = value.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*([\d.]+))?\s*\)/i);
        if (!m) return null;
        return { r: +m[1], g: +m[2], b: +m[3], a: m[4] !== undefined ? +m[4] : 1 };
    }

    private rgbToHex(r: number, g: number, b: number): string {
        return '#' + [r, g, b].map(v => v.toString(16).padStart(2, '0')).join('');
    }
}
